import { useQuery } from "@tanstack/react-query";
import { pick, sortBy } from "es-toolkit";
import naturalCompare from "string-natural-compare";
import AssetActions from "../../actions/AssetActions";
import UserActions from "../../actions/UserActions";
import AppConstants from "../../constants/AppConstants";
import { USER_ASSETS } from "../../constants/QueryKeys";
import UserService from "../../service/UserService";
import { queryClient, STORE_CACHE_DEFAULTS } from "../../utils/QueryUtils";
import { getUserId } from "./useAccount";
import { handleFolderActionFailed } from "./useAssetFolders";

/**
 * @typedef {import("@/types/data/Asset.mjs").Asset} Asset
 * @typedef {{ assets: Asset[] }} UserAssetsData
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

const defaults = STORE_CACHE_DEFAULTS;

// Register defaults on the shared client so non-hook callers get the same retry/staleTime.
queryClient.setQueryDefaults([USER_ASSETS], defaults);

/**
 * Loads the current user's uploaded assets (TanStack Query replacement for UserAssetsStore's `assets` list).
 * @param {QueryHookOptions<UserAssetsData>} [options] - Extra `useQuery` options, spread over the defaults.
 * @returns {{ assets: Asset[] | undefined } & QueryResultRest<UserAssetsData>}
 */
const useUserAssets = (options = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: [USER_ASSETS],
    queryFn: () => /** @type {Promise<UserAssetsData>} */ (UserService.loadAssets()),
    ...defaults,
    ...options,
  });
  return { assets: data?.assets, ...rest };
};

/**
 * One-shot, non-triggering read of the cached assets payload for non-React callers (Reflux stores).
 * @returns {UserAssetsData | undefined}
 */
export const getUserAssetsData = () =>
  /** @type {UserAssetsData | undefined} */ (queryClient.getQueryData([USER_ASSETS]));

/**
 * Writes the USER_ASSETS cache entry for non-React callers.
 * @param {(prev: UserAssetsData | undefined) => UserAssetsData} updater
 */
export const setUserAssetsData = (updater) => queryClient.setQueryData([USER_ASSETS], updater);

/**
 * Pure selector: the .woff font assets available for FontPanelUpload's font picker, reshaped to the `{ key, asset_name }` pairs it renders as options.
 * @param {Asset[]} [assets]
 * @returns {Array<{ key: string, asset_name: string }>}
 */
export const getWOFFList = (assets) =>
  (assets || [])
    .filter((asset) => asset.ext === AppConstants.EXT_WOFF)
    .map((asset) => ({ key: asset.key, asset_name: asset.asset_name }));

/**
 * Pure cache-updater: unshifts a server-confirmed asset upload onto the front of the list.
 * @param {UserAssetsData | undefined} prev
 * @param {Object} asset
 * @returns {UserAssetsData}
 */
export const insertUploadedAsset = (prev, asset) => {
  const finalAsset = pick(asset, [
    "asset_id",
    "key",
    "asset_name",
    "status",
    "asset_type",
    "aspect_ratio",
    "height",
    "width",
    "ext",
    "folder_id",
    "message",
  ]);
  if (!Object.hasOwn(finalAsset, "folder_id")) {
    finalAsset.folder_id = null;
  }

  return { ...prev, assets: [finalAsset, ...(prev?.assets ?? [])] };
};

/**
 * Pure cache-updater: removes the given assets from the list by asset_id.
 * @param {UserAssetsData | undefined} prev
 * @param {Asset[]} assetsToDelete
 * @returns {UserAssetsData}
 */
export const removeDeletedAssets = (prev, assetsToDelete) => {
  const deletedMap = {};
  assetsToDelete.forEach((asset) => {
    deletedMap[asset.asset_id] = true;
  });

  return {
    ...prev,
    assets: (prev?.assets ?? []).filter((asset) => !deletedMap[asset.asset_id]),
  };
};

/**
 * Pure cache-updater: sets `folder_id` on every asset whose id is in `assetIds`.
 * @param {UserAssetsData | undefined} prev
 * @param {Array<string>} assetIds
 * @param {string|number|null} folderId
 * @returns {UserAssetsData}
 */
export const moveAssetsToFolder = (prev, assetIds, folderId) => ({
  ...prev,
  assets: (prev?.assets ?? []).map((asset) =>
    assetIds.includes(asset.asset_id) ? { ...asset, folder_id: folderId } : asset,
  ),
});

/**
 * Pure sort: mirrors UserAssetsStore.sortBy's two branches -- natural-order comparison for `asset_name`, es-toolkit's default comparator for everything else.
 * @param {Asset[]} [assets]
 * @param {keyof Asset} sortAttribute
 * @returns {Asset[]}
 */
export const sortAssetsBy = (assets, sortAttribute) => {
  const list = assets ?? [];
  if (sortAttribute === "asset_name") {
    return [...list].sort((a, b) => naturalCompare(a.asset_name, b.asset_name));
  }
  return sortBy(list, [sortAttribute]);
};

/**
 * Sorts the cached asset list in place (cache-write) by `sortAttribute`.
 * @param {keyof Asset} sortAttribute
 */
export const sortUserAssets = (sortAttribute) => {
  setUserAssetsData((prev) => ({ ...prev, assets: sortAssetsBy(prev?.assets, sortAttribute) }));
};

/**
 * Calls `UserService.deleteAsset` per asset; failures are swallowed (legacy parity).
 * @param {Asset[]} assets
 * @returns {Promise<Array<{ asset_id: string, deleted: boolean }>>}
 */
export const deleteAssetsFromServer = (assets) =>
  Promise.all(
    assets.map((asset) => {
      const assetId = asset.asset_id;

      return UserService.deleteAsset(getUserId(), assetId)
        .then(() => ({ asset_id: assetId, deleted: true }))
        .catch(() => ({ asset_id: assetId, deleted: false }));
    }),
  );

// ---- Module-level action -> cache-write bridges.

UserActions.loadAssets.completed.listen((data) => {
  setUserAssetsData(() => ({ assets: data.assets }));
});

// Dispatched from AssetUploadStore outside any component lifecycle, hence a module listener.
AssetActions.completeAssetUpload.listen((asset) => {
  setUserAssetsData((prev) => insertUploadedAsset(prev, asset));
});

// Dispatched from AssetLibraryStore.onDeleteAssets, outside any component lifecycle.
AssetActions.deleteAssets.listen((data) => {
  const assetsToDelete = data.assets;

  // Optimistically remove -- if the server call below fails, the asset will
  // just reappear next time the list reloads (matches prior behavior).
  setUserAssetsData((prev) => removeDeletedAssets(prev, assetsToDelete));

  const serverAssets = assetsToDelete.filter(
    (asset) => asset.status !== AppConstants.UPLOAD_STATUS_ERROR,
  );

  if (serverAssets.length > 0) {
    deleteAssetsFromServer(serverAssets);
  }
});

AssetActions.moveToFolder.completed.listen((data) => {
  setUserAssetsData((prev) => moveAssetsToFolder(prev, data.assets, data.folder_id));
});
AssetActions.moveToFolder.failed.listen(handleFolderActionFailed);

export default useUserAssets;
